import React, { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { summary as fetchSummary } from "@/lib/track";
import { Eye, MessageCircle, Phone, MousePointerClick, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Analytics() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchSummary(Number(days)).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [days]);

  const top = useMemo(() => (data?.per_project || []).slice(0, 10), [data]);
  const totals = data?.totals || { views: 0, wa_clicks: 0, call_clicks: 0, ctr_pct: 0 };

  return (
    <DashboardShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline text-sg-gold-deep">VISITS · WHATSAPP CTR · LAST {days} DAYS</div>
          <h1 className="font-display text-4xl lg:text-5xl text-sg-navy mt-2">
            Your branded portal <span className="font-serif-italic text-sg-gold-deep">analytics.</span>
          </h1>
          <p className="text-sg-mute mt-2 max-w-2xl">
            Every visit, project view, and WhatsApp tap on your branded portal — attributed to you via UTM.
          </p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="md:w-44 rounded-full bg-white border-sg-line" data-testid="analytics-range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <KpiCard icon={<Eye className="w-5 h-5" />} label="Total Views" value={totals.views} testid="kpi-views" />
        <KpiCard icon={<MessageCircle className="w-5 h-5" />} label="WhatsApp Taps" value={totals.wa_clicks} testid="kpi-wa" />
        <KpiCard icon={<Phone className="w-5 h-5" />} label="Call Taps" value={totals.call_clicks} testid="kpi-calls" />
        <KpiCard
          icon={<MousePointerClick className="w-5 h-5" />}
          label="WhatsApp CTR"
          value={`${totals.ctr_pct ?? 0}%`}
          accent
          testid="kpi-ctr"
        />
      </div>

      {loading && (
        <div className="mt-10 text-sm text-sg-mute animate-pulse">Loading analytics…</div>
      )}

      {!loading && totals.views === 0 && totals.wa_clicks === 0 ? (
        <div className="mt-8 bg-white border border-dashed border-sg-line rounded-3xl p-10 text-center" data-testid="analytics-empty">
          <TrendingUp className="w-10 h-10 mx-auto text-sg-gold-deep" />
          <p className="mt-4 font-display text-2xl text-sg-navy">No traffic yet.</p>
          <p className="text-sm text-sg-mute mt-2">
            Share your branded portal link — visits and WhatsApp taps appear here in real-time.
          </p>
        </div>
      ) : null}

      {/* Daily trend */}
      {!loading && data?.daily_series?.length > 0 && (totals.views || totals.wa_clicks) ? (
        <div className="mt-10 bg-white border border-sg-line rounded-3xl p-6" data-testid="daily-trend-card">
          <div className="flex items-center justify-between">
            <div className="overline text-sg-gold-deep">DAILY TREND</div>
          </div>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.daily_series} margin={{ top: 8, right: 16, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7DCC1" />
                <XAxis dataKey="day" stroke="#5B6982" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#5B6982" tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E7DCC1" }} />
                <Legend />
                <Line type="monotone" dataKey="views" name="Views" stroke="#0B1B33" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="wa_clicks" name="WhatsApp" stroke="#C9A24A" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="call_clicks" name="Calls" stroke="#A98532" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : null}

      {/* Per-project chart + table */}
      {!loading && top.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-10">
          <div className="lg:col-span-3 bg-white border border-sg-line rounded-3xl p-6" data-testid="per-project-chart">
            <div className="overline text-sg-gold-deep mb-3">VIEWS VS WHATSAPP · TOP PROJECTS</div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={top} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7DCC1" />
                  <XAxis dataKey="project_name" stroke="#5B6982" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={56} interval={0} />
                  <YAxis stroke="#5B6982" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E7DCC1" }} />
                  <Legend />
                  <Bar dataKey="views" name="Views" fill="#0B1B33" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="wa_clicks" name="WhatsApp" fill="#C9A24A" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white border border-sg-line rounded-3xl overflow-hidden" data-testid="per-project-table">
            <div className="px-5 py-4 border-b border-sg-line overline text-sg-gold-deep">PER-PROJECT BREAKDOWN</div>
            <div className="divide-y divide-sg-line max-h-72 overflow-auto">
              {top.map((r) => (
                <div key={r.project_id} className="grid grid-cols-[1fr_auto] gap-3 px-5 py-3 text-sm items-center" data-testid={`row-${r.project_id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sg-navy truncate">{r.project_name}</div>
                    <div className="text-xs text-sg-mute">{r.locality_tag} · {r.views} views · {r.wa_clicks} WA</div>
                  </div>
                  <div className="text-right">
                    <div className="rupee text-lg text-sg-navy">{r.ctr_pct}%</div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-sg-mute">CTR</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </DashboardShell>
  );
}

function KpiCard({ icon, label, value, accent, testid }) {
  return (
    <div className={`rounded-3xl border p-5 ${accent ? "bg-sg-navy text-white border-sg-navy" : "bg-white border-sg-line"}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${accent ? "bg-sg-gold/20 text-sg-gold" : "bg-sg-cream-soft text-sg-gold-deep"}`}>
          {icon}
        </div>
        <div className={`overline ${accent ? "text-sg-gold" : "text-sg-mute"}`}>{label}</div>
      </div>
      <div className={`rupee text-4xl mt-3 ${accent ? "text-sg-gold" : "text-sg-navy"}`}>{value}</div>
    </div>
  );
}
